import { type CSSProperties, type ReactNode, useId } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// KpiTile / Stat / StatGrid / Sparkline (design/components/kpi.html). Numbers
// that matter, presented calmly. The delta is communicated by ARROW + TEXT,
// never green/red alone, and its colour follows MEANING (down can be good,
// e.g. latency) via goodWhen. Sparklines are decorative (aria-hidden); the
// value + trend live in accessible text. Tabular figures so they don't dance.

const sparkColor = {
  brand: 'var(--brand-blue)',
  ok: 'var(--ok)',
  sens: 'var(--sens)',
}

export interface SparklineProps {
  points: number[]
  tone?: 'brand' | 'ok' | 'sens'
  /** viewBox dims; CSS controls the rendered size. */
  vbWidth?: number
  vbHeight?: number
  className?: string
}

/** Decorative (aria-hidden) trend line with gradient fill — the accessible
 *  value/trend must live in surrounding text. Under 2 points renders an empty
 *  span. */
export function Sparkline({
  points,
  tone = 'brand',
  vbWidth = 120,
  vbHeight = 34,
  className,
}: SparklineProps) {
  const gradId = useId()
  const color = sparkColor[tone]

  if (points.length < 2) return <span aria-hidden className={cn('block', className)} />

  const max = Math.max(...points)
  const min = Math.min(...points)
  const diff = max - min
  const range = diff === 0 ? 1 : diff
  const stepX = vbWidth / (points.length - 1)
  // 2px top/bottom padding so the stroke isn't clipped
  const pad = 2
  const coords = points.map((p, i) => {
    const x = i * stepX
    const y = pad + (1 - (p - min) / range) * (vbHeight - pad * 2)
    return [Number(x.toFixed(2)), Number(y.toFixed(2))] as const
  })
  const line = coords.map(([x, y]) => `${x},${y}`).join(' ')
  const area = `M${coords.map(([x, y]) => `${x} ${y}`).join(' L')} L${vbWidth} ${vbHeight} L0 ${vbHeight} Z`

  return (
    <svg
      aria-hidden
      viewBox={`0 0 ${vbWidth} ${vbHeight}`}
      preserveAspectRatio="none"
      className={cn('block h-[34px] w-full', className)}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.22" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ArrowUpRight() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-3"
    >
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  )
}
function ArrowDownRight() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-3"
    >
      <path d="M7 7 17 17M17 9v8H9" />
    </svg>
  )
}

export interface KpiDelta {
  dir: 'up' | 'down' | 'flat'
  text: string
  note?: string
  /** Which direction is "good"; colour follows meaning. Default 'up'. */
  goodWhen?: 'up' | 'down'
}

export interface KpiTileProps {
  label: string
  value: string
  delta?: KpiDelta
  sparkline?: number[]
  sparklineTone?: 'brand' | 'ok' | 'sens'
  className?: string
}

/** Headline metric card: label, tabular value, optional delta + Sparkline.
 *  The delta reads as arrow + text (sr-only direction word), never colour
 *  alone, and its colour follows MEANING via `goodWhen` (down can be good). */
export function KpiTile({
  label,
  value,
  delta,
  sparkline,
  sparklineTone = 'brand',
  className,
}: KpiTileProps) {
  const { t } = useTranslation('components')
  let deltaColor = 'text-text-2'
  if (delta && delta.dir !== 'flat') {
    const good = delta.dir === (delta.goodWhen ?? 'up')
    deltaColor = good ? 'text-ok' : 'text-sens'
  }
  const dirWord = delta
    ? `${t(`ui.kpi.${delta.dir === 'up' ? 'up' : delta.dir === 'down' ? 'down' : 'none'}`)} `
    : ''

  return (
    <div className={cn('rounded-lg border border-line bg-surface px-[18px] py-4', className)}>
      <div className="text-[11.5px] font-medium text-text-2">{label}</div>
      <div className="mt-1.5 text-[28px] font-extrabold leading-[1.05] tracking-[-0.02em] tabular-nums text-text">
        {value}
      </div>
      {(delta != null || sparkline != null) && (
        <div className="mt-[9px] flex items-end gap-3">
          {delta && (
            <span
              className={cn(
                'inline-flex items-center gap-[3px] whitespace-nowrap text-[11.5px] font-bold',
                deltaColor,
              )}
            >
              {delta.dir === 'up' && <ArrowUpRight />}
              {delta.dir === 'down' && <ArrowDownRight />}
              {delta.dir === 'flat' && <span aria-hidden>−</span>}
              <span className="sr-only">{dirWord}</span>
              {delta.text}
            </span>
          )}
          {delta?.note && (
            <span className="text-[11.5px] font-medium text-text-2">{delta.note}</span>
          )}
          {sparkline && (
            <Sparkline
              points={sparkline}
              tone={sparklineTone}
              className="h-[34px] min-w-[60px] flex-1"
            />
          )}
        </div>
      )}
    </div>
  )
}

export interface StatProps {
  value: string
  label: string
  className?: string
}

/** Bare tabular value + caption pair; compose inside StatGrid for a divided
 *  stat strip. */
export function Stat({ value, label, className }: StatProps) {
  return (
    <div className={cn('flex flex-col gap-[3px]', className)}>
      <span className="text-[19px] font-extrabold leading-none tracking-[-0.01em] tabular-nums text-text">
        {value}
      </span>
      <span className="text-[11px] text-text-2">{label}</span>
    </div>
  )
}

export interface StatGridProps {
  children: ReactNode
  columns?: number
  className?: string
}

/** Divided strip of Stat cells: 1 column on phone, 2 on sm+, the full
 *  `columns` at lg+; divider borders stay clean across wraps. */
export function StatGrid({ children, columns = 3, className }: StatGridProps) {
  // Responsive: 1 column on phone, 2 on sm+, the full `columns` at lg+ — so a
  // 3- or 4-up stat strip reflows instead of overflowing on narrow screens.
  return (
    <div
      className={cn(
        'grid grid-cols-1 overflow-hidden rounded-[11px] border border-line bg-surface sm:grid-cols-2',
        'lg:[grid-template-columns:repeat(var(--sg-cols),minmax(0,1fr))]',
        // dividers: row border below every cell except the last row; right
        // border between cells, reset at each row end so wraps stay clean.
        '[&>*]:border-b [&>*]:border-line [&>*]:px-4 [&>*]:py-[15px]',
        'sm:[&>*:nth-last-child(-n+2):nth-child(even)]:border-b-0 sm:[&>*:last-child]:border-b-0',
        className,
      )}
      style={{ '--sg-cols': columns } as CSSProperties}
    >
      {children}
    </div>
  )
}
